import { useNavigate } from 'react-router-dom';
import { ArrowUpDown, ChevronRight } from 'lucide-react';
import { useState, useMemo, useCallback, memo } from 'react';
import { Pagination } from '../Pagination';
import ProductCard from '../../../utils/productCard/ProductCard';
import ProductCardSkeleton from '../../../ui/components/skeletons/ProductCardSkeleton';
import { ProductCategoriesShortcut } from '../productCategoriesShortcut/ProductCategoriesShortcut';
import { useSubCategoryPage } from './useSubCategoryPage';
import ProductFilterForm from '../filterForms/ProductFilterForm';

const SubCategoryPage = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    brand: [],
    minPrice: 100,
    maxPrice: 300000,
    downpayment: '',
    sortBy: '',
  });

  const {
    category,
    sub_category,
    page,
    setPage,
    totalPages,
    totalSubCategoryProducts,
    products,
    isFetchingProducts,
    isProductsError,
    refetchProducts,
  } = useSubCategoryPage();

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      filters.brand?.length > 0 ||
      filters.minPrice !== 100 ||
      filters.maxPrice !== 300000 ||
      filters.downpayment ||
      filters.sortBy
    );
  }, [filters]);

  // Filter and sort products only if filters are active
  const filteredProducts = useMemo(() => {
    if (!products) return [];

    // If no filters are active, return original products (server-side pagination)
    if (!hasActiveFilters) {
      return products;
    }

    let filtered = [...products];

    // Filter by brand (case-insensitive)
    if (filters.brand?.length > 0) {
      const normalizedFilterBrands = filters.brand.map((b) =>
        b.toLowerCase().trim()
      );
      filtered = filtered.filter((product) => {
        const productBrand = product?.productBrandName?.toLowerCase().trim();
        return normalizedFilterBrands.some(
          (filterBrand) =>
            productBrand === filterBrand ||
            productBrand?.startsWith(filterBrand)
        );
      });
    }

    // Filter by price range
    if (filters.minPrice !== 100 || filters.maxPrice !== 300000) {
      filtered = filtered.filter((product) => {
        const price =
          product?.fairAppSalesPrice && product.fairAppSalesPrice > 0
            ? product.fairAppSalesPrice
            : product?.fairAppPrice || product?.price || 0;
        return price >= filters.minPrice && price <= filters.maxPrice;
      });
    }

    // Filter by downpayment percentage
    if (filters.downpayment) {
      const downpaymentPercent = parseInt(filters.downpayment);
      filtered = filtered.filter((product) => {
        const minDownpaymentStr = product?.minimumDownPaymentPercentage;
        if (!minDownpaymentStr) return false;
        // Handle string format like "35.00" or number format
        const minDownpayment =
          typeof minDownpaymentStr === 'string'
            ? parseInt(minDownpaymentStr.split('.')[0])
            : parseInt(minDownpaymentStr);
        return minDownpayment === downpaymentPercent;
      });
    }

    // Sort products
    if (filters.sortBy) {
      switch (filters.sortBy) {
        case 'newly-listed-desc':
          // Newest first (descending order)
          filtered.sort((a, b) => {
            const dateA = new Date(a?.createdAt?.replace(' ', 'T') || 0);
            const dateB = new Date(b?.createdAt?.replace(' ', 'T') || 0);
            return dateB - dateA;
          });
          break;
        case 'newly-listed-asc':
          // Oldest first (ascending order)
          filtered.sort((a, b) => {
            const dateA = new Date(a?.createdAt?.replace(' ', 'T') || 0);
            const dateB = new Date(b?.createdAt?.replace(' ', 'T') || 0);
            return dateA - dateB;
          });
          break;
        case 'price-low-high':
          filtered.sort((a, b) => {
            const priceA =
              a?.fairAppSalesPrice && a.fairAppSalesPrice > 0
                ? a.fairAppSalesPrice
                : a?.fairAppPrice || a?.price || 0;
            const priceB =
              b?.fairAppSalesPrice && b.fairAppSalesPrice > 0
                ? b.fairAppSalesPrice
                : b?.fairAppPrice || b?.price || 0;
            return priceA - priceB;
          });
          break;
        case 'price-high-low':
          filtered.sort((a, b) => {
            const priceA =
              a?.fairAppSalesPrice && a.fairAppSalesPrice > 0
                ? a.fairAppSalesPrice
                : a?.fairAppPrice || a?.price || 0;
            const priceB =
              b?.fairAppSalesPrice && b.fairAppSalesPrice > 0
                ? b.fairAppSalesPrice
                : b?.fairAppPrice || b?.price || 0;
            return priceB - priceA;
          });
          break;
        case 'popular':
          filtered.sort(
            (a, b) => (b?.average_rating || 0) - (a?.average_rating || 0)
          );
          break;
        default:
          // Keep original order from API
          break;
      }
    }

    return filtered;
  }, [products, filters, hasActiveFilters]);

  const handleFilter = useCallback((filterData) => {
    setFilters(filterData);
  }, []);

  // Only show skeletons when there is nothing to render yet; a background
  // refetch keeps the current cards on screen.
  const showSkeleton = isFetchingProducts && !products?.length;
  // Failed with nothing to show (common on flaky connections): offer a retry
  // instead of claiming the category is empty.
  const showError = isProductsError && !products?.length && !isFetchingProducts;

  return (
    <div className="lg:mx-[60px] mb-20">
      <div className="flex px-0 overflow-x-auto">
        <ProductCategoriesShortcut />
      </div>

      <section className="px-5 lg:mx-0">
        <Breadcrumb
          category={category}
          subCategory={sub_category}
          onNavigateHome={() => navigate('/')}
          onNavigateBack={() => navigate(-1)}
        />

        <PageHeader
          subCategory={sub_category}
          productCount={
            hasActiveFilters ? filteredProducts?.length : products?.length
          }
          totalSubCategoryProducts={totalSubCategoryProducts}
          isLoading={showSkeleton}
        />

        <hr />
        <hr className="mb-6" />

        {products?.length > 0 && (
          <ProductFilterForm products={products} handleFilter={handleFilter} />
        )}

        {showSkeleton && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {Array.from({ length: 10 }).map((_, index) => (
              <ProductCardSkeleton key={index} />
            ))}
          </div>
        )}
        {filteredProducts?.length > 0 && (
          <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredProducts.map((product, index) => (
              <div key={product.productID ?? product.id} className="mb-6">
                {/* First row (2 cards on mobile, up to 5 on desktop) */}
                <ProductCard product={product} priority={index < 4} />
              </div>
            ))}
          </section>
        )}
        {showError && (
          <div className="text-center rounded-lg mt-10 bg-gray-100 w-full py-16 flex flex-col items-center gap-4">
            <p className="text-[#222224]">
              We couldn&apos;t load these products. Please check your
              connection.
            </p>
            <button
              type="button"
              onClick={() => refetchProducts()}
              className="rounded-full bg-[#222224] text-white text-sm px-6 py-2"
            >
              Try again
            </button>
          </div>
        )}
        {!filteredProducts?.length && !showSkeleton && !showError && (
          <p className="text-center text-white   rounded-lg mt-10 bg-gray-400 w-full py-20 flex justify-center items-center">
            No products found matching your filters.
          </p>
        )}

        {filteredProducts?.length > 0 && !hasActiveFilters && (
          <div className="flex justify-center md:justify-start">
            <Pagination page={page} setPage={setPage} totalPages={totalPages} />
          </div>
        )}
      </section>
    </div>
  );
};

const Breadcrumb = memo(
  ({ category, subCategory, onNavigateHome, onNavigateBack }) => (
    <div className="flex space-x-1 items-center mt-4">
      <p
        className="text-[#222224] text-sm cursor-pointer"
        onClick={onNavigateHome}
      >
        SmallSmall
      </p>
      <ChevronRight size={11} className="text-[#6B6B6B]" />
      <p
        className="text-[#222224] text-sm cursor-pointer"
        onClick={onNavigateBack}
      >
        {category?.charAt(0).toUpperCase() + category?.slice(1)}
      </p>
      <ChevronRight size={11} className="text-[#6B6B6B]" />
      <p className="text-[#6B6B6B] text-sm">
        {subCategory?.charAt(0).toUpperCase() + subCategory?.slice(1)}
      </p>
    </div>
  )
);
Breadcrumb.displayName = 'Breadcrumb';

const PageHeader = memo(
  ({ subCategory, productCount, totalSubCategoryProducts, isLoading }) => (
    <div className="flex justify-between items-baseline">
      <header className="mt-8 mb-5 flex items-baseline space-x-2">
        <h1 className="text-[25px] capitalize">
          {subCategory?.split('-').join(' ')}
        </h1>
        {/* Hide the count while loading rather than showing "(0) items" */}
        {!isLoading && (
          <p className="text-xs text-[#6B6B6B] flex items-center space-x-1">
            <span>({totalSubCategoryProducts || 0})</span>
            <span>{totalSubCategoryProducts === 1 ? 'item' : 'items'}</span>
          </p>
        )}
      </header>
      <div
        className="flex md:hidden items-center cursor-pointer hover:shadow-[0px_4px_0px_rgba(0,0,0,0.2)] hover:translate-x-1 transition-all duration-300 ease-in-out"
        role="button"
        aria-label="Sort products by subcategory"
      >
        <p className="font-semibold text-sm">Sort</p>
        <span>
          <ArrowUpDown size={18} />
        </span>
      </div>
    </div>
  )
);
PageHeader.displayName = 'PageHeader';

const ProductGridSkeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
    {Array.from({ length: 10 }).map((_, index) => (
      <ProductCardSkeleton key={index} />
    ))}
  </div>
);

const ProductGrid = ({ products }) => (
  <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
    {products?.map((product) => (
      <div key={product.productID} className="mb-6">
        <ProductCard product={product} />
      </div>
    ))}
  </section>
);

export default SubCategoryPage;
